// src/pages/HomePage.js
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { FloatingContactContext } from '../components/FloatingContact';
import '../styles/HomePage.css';

const HomePage = () => {
  const { handleOpenContact } = useContext(FloatingContactContext);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay"></div>
          <div className="container hero-content">
            <h1 className="hero-title">ENTREPRENEURSHIP IS OUR FREEDOM</h1>
            <p className="hero-tagline">LLCs, Corporations, and Compliance Services to Help You Build with Confidence</p>
            <p className="hero-description">
              Personalized support for entrepreneurs looking to establish their business on solid legal ground
            </p>
            <div className="hero-buttons">
              <Link to="/service-form" className="btn btn-secondary">START YOUR BUSINESS TODAY</Link>
              <button onClick={handleOpenContact} className="btn btn-outline">FREE CONSULTATION</button>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="what-we-do-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What We Do</h2>
            <div className="title-underline"></div>
          </div>
          
          <div className="services-intro">
            <p>
              At Legate, we provide accessible, hands-on support for entrepreneurs at every stage of their business formation journey. We focus on creating the right legal structures to protect your assets while setting you up for long-term success.
            </p>
          </div>
          
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon-wrapper">
                <i className="fas fa-building"></i>
              </div>
              <div className="service-content">
                <h3 className="service-title">LLC Formation</h3>
                <p className="service-description">
                  Establish your business as an LLC with complete liability protection, customized operating agreements, and state compliance guidance.
                </p>
                <Link to="/services#llc" className="service-link">
                  <span>Learn More</span>
                  <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            </div>
            
            <div className="service-card">
              <div className="service-icon-wrapper">
                <i className="fas fa-landmark"></i>
              </div>
              <div className="service-content">
                <h3 className="service-title">Corporation Setup</h3>
                <p className="service-description">
                  Form your S-Corporation or C-Corporation with expertly prepared documentation, shareholder agreements, and governance structures.
                </p>
                <Link to="/services#corporation" className="service-link">
                  <span>Learn More</span>
                  <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            </div>
            
            <div className="service-card">
              <div className="service-icon-wrapper">
                <i className="fas fa-clipboard-check"></i>
              </div>
              <div className="service-content">
                <h3 className="service-title">Compliance Services</h3>
                <p className="service-description">
                  Maintain your business in good standing with ongoing compliance monitoring, annual report filing, and regulatory updates.
                </p>
                <Link to="/services#compliance" className="service-link">
                  <span>Learn More</span>
                  <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-us-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Legate</h2>
            <div className="title-underline"></div>
          </div>
          
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-handshake"></i>
              </div>
              <h3 className="benefit-title">Personalized Support</h3>
              <p className="benefit-description">
                Work directly with experts who understand your unique business needs, not an automated platform.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="benefit-title">Complete Protection</h3>
              <p className="benefit-description">
                Receive comprehensive legal structures that protect your personal assets and minimize liability.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-file-signature"></i>
              </div>
              <h3 className="benefit-title">Custom Documents</h3>
              <p className="benefit-description">
                Get tailored operating agreements and formation documents specific to your business needs.
              </p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="benefit-title">Long-Term Planning</h3>
              <p className="benefit-description">
                Build a foundation that supports growth with scalable legal structures and compliance strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <div className="title-underline"></div>
          </div>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3 className="step-title">Free Consultation</h3>
                <p className="step-description">
                  We'll discuss your business goals and help determine the right legal structure.
                </p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3 className="step-title">Custom Documentation</h3>
                <p className="step-description">
                  We prepare all necessary documents tailored to your specific business needs.
                </p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3 className="step-title">Filing & Registration</h3>
                <p className="step-description">
                  We handle all state filings, registrations, and ensure full legal compliance.
                </p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3 className="step-title">Ongoing Support</h3>
                <p className="step-description">
                  We provide continued guidance to keep your business legally compliant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Our Clients Say</h2>
            <div className="title-underline"></div>
          </div>
          
          <div className="testimonial-slider">
            <div className="testimonial">
              <div className="testimonial-content">
                <p className="testimonial-text">
                  "Working with Legate transformed my business idea into reality. Their guidance through the formation process was invaluable, and their continued support has been essential to our growth."
                </p>
                <div className="testimonial-author">
                  <h4 className="author-name">Sarah Johnson</h4>
                  <p className="author-business">Tech Startup Founder</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Build Your Business on Solid Ground?</h2>
            <p className="cta-text">
              Take the first step toward protecting your assets and setting your business up for success.
            </p>
            <div className="cta-buttons">
              <Link to="/service-form" className="btn btn-secondary">Start Your Business</Link>
              <button onClick={handleOpenContact} className="btn btn-outline-light">Free Consultation</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
